import React from 'react';
import { cva } from 'class-variance-authority';
import { cn } from '../../utils/cn';
import { Pie, Doughnut, Line, Bar } from 'react-chartjs-2';
import { Chart as ChartJS, ArcElement, Tooltip, Legend, CategoryScale, LinearScale, PointElement, LineElement, BarElement } from 'chart.js';
import { designTokens, getColor } from '../../tokens';

// Register Chart.js components
ChartJS.register(ArcElement, Tooltip, Legend, CategoryScale, LinearScale, PointElement, LineElement, BarElement);

// Design system colors for charts - both light and dark mode
const CHART_COLORS = {
  primary: [
    designTokens.colors.primary[600],
    designTokens.colors.secondary[600], 
    designTokens.colors.feedback.warning,
    designTokens.colors.feedback.danger,
    designTokens.colors.accent[500],
    designTokens.colors.cyan[500],
    designTokens.colors.feedback.success,
    designTokens.colors.primary[400]
  ],
  backgrounds: {
    primary: `${designTokens.colors.primary[600]}20`,
    secondary: `${designTokens.colors.secondary[600]}20`,
    success: `${designTokens.colors.feedback.success}20`,
    warning: `${designTokens.colors.feedback.warning}20`,
    danger: `${designTokens.colors.feedback.danger}20`,
  }
};

// Chart Container Component
const chartVariants = cva(
  "bg-white dark:bg-dark-200 rounded-xl border border-gray-200 dark:border-dark-300 overflow-hidden",
  {
    variants: {
      variant: {
        default: "shadow-sm",
        elevated: "shadow-lg",
        gradient: "bg-gradient-to-br from-white to-gray-50 dark:from-dark-200 dark:to-dark-100",
      },
      padding: {
        none: "p-0",
        sm: "p-4",
        default: "p-6",
        lg: "p-8",
      },
    },
    defaultVariants: {
      variant: "default",
      padding: "default",
    },
  }
);

export const ChartContainer = React.forwardRef(({
  className,
  variant,
  padding,
  children,
  ...props
}, ref) => {
  return (
    <div
      ref={ref}
      className={cn(chartVariants({ variant, padding }), className)}
      {...props}
    >
      {children}
    </div>
  );
});

ChartContainer.displayName = "ChartContainer";

// Chart Header Component
export const ChartHeader = React.forwardRef(({
  className,
  title,
  subtitle,
  action,
  children,
  ...props
}, ref) => {
  return (
    <div
      ref={ref}
      className={cn("flex items-center justify-between mb-6", className)}
      {...props}
    >
      <div>
        {title && (
          <h3 className="text-lg font-semibold text-gray-900 dark:text-dark-900">
            {title}
          </h3>
        )}
        {subtitle && (
          <p className="text-sm text-gray-500 dark:text-dark-600 mt-1">
            {subtitle}
          </p>
        )}
      </div>
      {action && <div className="flex-shrink-0">{action}</div>}
      {children}
    </div>
  );
});

ChartHeader.displayName = "ChartHeader";

// Modern Stat Card Component
export const StatCard = React.forwardRef(({
  className,
  value,
  label,
  change,
  changeType = "positive",
  icon,
  iconColor = "primary",
  trend,
  ...props
}, ref) => {
  const getIconBgClasses = () => {
    switch (iconColor) {
      case "primary": return "bg-blue-100 text-blue-600 dark:bg-blue-500/20 dark:text-blue-400";
      case "success": return "bg-green-100 text-green-600 dark:bg-green-500/20 dark:text-green-400";
      case "warning": return "bg-yellow-100 text-yellow-600 dark:bg-yellow-500/20 dark:text-yellow-400";
      case "danger": return "bg-red-100 text-red-600 dark:bg-red-500/20 dark:text-red-400";
      case "purple": return "bg-purple-100 text-purple-600 dark:bg-purple-500/20 dark:text-purple-400";
      default: return "bg-gray-100 text-gray-600 dark:bg-gray-500/20 dark:text-gray-400";
    }
  };

  const getChangeColorClasses = () => {
    if (changeType === "positive") return "text-green-600 dark:text-green-400";
    if (changeType === "negative") return "text-red-600 dark:text-red-400";
    return "text-gray-600 dark:text-gray-400";
  };

  return (
    <div
      ref={ref}
      className={cn(
        "bg-white dark:bg-dark-200 rounded-xl p-6 border border-gray-200 dark:border-dark-300 shadow-sm hover:shadow-md transition-shadow duration-200",
        className
      )}
      {...props}
    >
      <div className="flex items-center justify-between">
        <div className="flex-1">
          <div className="flex items-center justify-between mb-2">
            <p className="text-sm font-medium text-gray-600 dark:text-gray-400">
              {label}
            </p>
            {icon && (
              <div className={cn("p-2 rounded-lg", getIconBgClasses())}>
                {icon}
              </div>
            )}
          </div>
          <div className="flex items-baseline space-x-2">
            <p className="text-2xl font-bold text-gray-900 dark:text-white">
              {value}
            </p>
            {change && (
              <span className={cn("text-sm font-medium flex items-center", getChangeColorClasses())}>
                <i className={cn("fa-solid mr-1", 
                  changeType === "positive" ? "fa-arrow-up" : 
                  changeType === "negative" ? "fa-arrow-down" : "fa-minus"
                )} />
                {change}
              </span>
            )}
          </div>
          {trend && (
            <p className="text-xs mt-1 text-gray-500 dark:text-gray-400">
              {trend}
            </p>
          )}
        </div>
      </div>
    </div>
  );
});

StatCard.displayName = "StatCard";

// Modern Pie Chart Component
export const ModernPieChart = React.forwardRef(({
  className,
  data,
  options = {},
  title,
  subtitle,
  showLegend = true,
  loading = false,
  colors = CHART_COLORS.primary,
  ...props
}, ref) => {
  // Debug: Log the data to see what's being passed
  React.useEffect(() => {
    console.log('Pie chart data:', data);
    console.log('Pie chart loading:', loading);
  }, [data, loading]);

  const defaultOptions = {
    responsive: true,
    maintainAspectRatio: false,
    plugins: {
      legend: {
        display: showLegend,
        position: 'bottom',
        labels: {
          padding: 8, // Reduced padding
          usePointStyle: true,
          font: {
            size: 11,
            family: designTokens.typography.fontFamily.primary.join(', '),
          },
          color: designTokens.colors.neutral[500],
          boxWidth: 8,
          boxHeight: 8,
        },
      },
      tooltip: {
        backgroundColor: `${designTokens.colors.neutral[900]}e6`,
        titleColor: designTokens.colors.semantic.text.inverse,
        bodyColor: designTokens.colors.semantic.text.inverse,
        borderColor: `${designTokens.colors.neutral[200]}1a`,
        borderWidth: 1,
        cornerRadius: parseInt(designTokens.borderRadius.lg),
        padding: parseInt(designTokens.spacing[3]),
        titleFont: {
          size: 14,
          family: designTokens.typography.fontFamily.primary.join(', '),
          weight: designTokens.typography.fontWeight.semibold,
        },
        bodyFont: {
          size: 13,
          family: designTokens.typography.fontFamily.primary.join(', '),
        },
      },
    },
    // Remove layout padding
  };

  // Prepare chart data with better error handling
  const prepareChartData = () => {
    if (!data || !data.datasets || !data.datasets[0] || !data.datasets[0].data) {
      console.log('Invalid data structure, returning null');
      return null;
    }

    const dataset = data.datasets[0];
    const labels = data.labels || [];
    const values = dataset.data || [];

    if (labels.length === 0 || values.length === 0) {
      console.log('Empty labels or values');
      return null;
    }

    return {
      labels: labels,
      datasets: [
        {
          data: values,
          backgroundColor: dataset.backgroundColor || colors.slice(0, values.length),
          borderColor: dataset.borderColor || designTokens.colors.primary[600],
          borderWidth: dataset.borderWidth || 2,
          hoverBackgroundColor: dataset.backgroundColor || colors.slice(0, values.length),
          hoverBorderColor: dataset.borderColor || designTokens.colors.primary[600],
          hoverBorderWidth: 3,
        }
      ]
    };
  };

  const chartData = prepareChartData();
  const hasValidData = chartData !== null;

  return (
    <ChartContainer ref={ref} className={className} {...props}>
      {(title || subtitle) && (
        <ChartHeader title={title} subtitle={subtitle} />
      )}
      <div className={cn(
        "relative",
        showLegend ? "h-80" : "h-64" // Reduced height significantly (was h-[420px])
      )}>
        {loading ? (
          <div className="flex items-center justify-center h-full">
            <div className="text-center">
              <div 
                className="animate-spin rounded-full h-8 w-8 border-b-2 mx-auto mb-2"
                style={{ borderColor: designTokens.colors.primary[600] }}
              ></div>
              <p className="text-sm text-gray-500 dark:text-gray-400">Loading chart...</p>
            </div>
          </div>
        ) : hasValidData ? (
          <div className="w-full h-full flex items-center justify-center">
            {/* Single centered container for the chart - Chart.js will handle legend positioning */}
            <div className="w-full h-full max-w-[300px]"> {/* Reduced from max-w-[380px] */}
              <Pie 
                data={chartData} 
                options={{ ...defaultOptions, ...options }}
              />
            </div>
          </div>
        ) : (
          <div className="flex items-center justify-center h-full">
            <div className="text-center">
              <div className="w-16 h-16 mx-auto mb-4 rounded-full border-4 border-gray-200 dark:border-gray-700 flex items-center justify-center">
                <i className="fa-solid fa-chart-pie text-2xl text-gray-400 dark:text-gray-500" />
              </div>
              <p className="text-sm text-gray-500 dark:text-gray-400">No data available</p>
              <p className="text-xs mt-1 text-gray-400 dark:text-gray-500">Chart will appear when data is loaded</p>
            </div>
          </div>
        )}
      </div>
    </ChartContainer>
  );
});

ModernPieChart.displayName = "ModernPieChart";

// Modern Line Chart Component
export const ModernLineChart = React.forwardRef(({
  className,
  data,
  options = {},
  title,
  subtitle,
  gradient = true,
  smooth = true,
  showGrid = true,
  loading = false,
  ...props
}, ref) => {
  const defaultOptions = {
    responsive: true,
    maintainAspectRatio: false,
    interaction: {
      mode: 'index',
      intersect: false,
    },
    plugins: {
      legend: {
        display: true,
        position: 'top',
        align: 'end',
        labels: {
          padding: parseInt(designTokens.spacing[5]),
          usePointStyle: true,
          font: {
            size: 12,
            family: designTokens.typography.fontFamily.primary.join(', '),
          },
          color: designTokens.colors.neutral[500],
        },
      },
      tooltip: {
        backgroundColor: `${designTokens.colors.neutral[900]}e6`,
        titleColor: designTokens.colors.semantic.text.inverse,
        bodyColor: designTokens.colors.semantic.text.inverse,
        borderColor: `${designTokens.colors.neutral[200]}1a`,
        borderWidth: 1,
        cornerRadius: parseInt(designTokens.borderRadius.lg),
        padding: parseInt(designTokens.spacing[3]),
        titleFont: {
          size: 14,
          family: designTokens.typography.fontFamily.primary.join(', '),
          weight: designTokens.typography.fontWeight.semibold,
        },
        bodyFont: {
          size: 13,
          family: designTokens.typography.fontFamily.primary.join(', '),
        },
      },
    },
    scales: {
      x: {
        display: true,
        grid: {
          display: showGrid,
          color: `${designTokens.colors.neutral[400]}1a`,
          drawBorder: false,
        },
        ticks: {
          color: designTokens.colors.neutral[500],
          font: {
            size: 11,
            family: designTokens.typography.fontFamily.primary.join(', '),
          },
        },
      },
      y: {
        display: true,
        grid: {
          display: showGrid,
          color: `${designTokens.colors.neutral[400]}1a`,
          drawBorder: false,
        },
        ticks: {
          color: designTokens.colors.neutral[500],
          font: {
            size: 11,
            family: designTokens.typography.fontFamily.primary.join(', '),
          },
        },
      },
    },
    elements: {
      point: {
        radius: 4,
        hoverRadius: 6,
        borderWidth: 2,
      },
      line: {
        tension: smooth ? 0.4 : 0,
        borderWidth: 3,
      },
    },
  };

  const chartData = {
    ...data,
    datasets: data.datasets?.map((dataset, index) => ({
      ...dataset,
      fill: gradient,
      backgroundColor: gradient && dataset.backgroundColor 
        ? `${dataset.backgroundColor}20` 
        : dataset.backgroundColor,
      pointBackgroundColor: dataset.borderColor || designTokens.colors.primary[600],
      pointBorderColor: designTokens.colors.primary[600],
    })),
  };

  return (
    <ChartContainer ref={ref} className={className} {...props}>
      {(title || subtitle) && (
        <ChartHeader title={title} subtitle={subtitle} />
      )}
      <div className="relative h-80">
        {loading ? (
          <div className="flex items-center justify-center h-full">
            <div className="text-center">
              <div 
                className="animate-spin rounded-full h-8 w-8 border-b-2 mx-auto mb-2"
                style={{ borderColor: designTokens.colors.primary[600] }}
              ></div>
              <p className="text-sm text-gray-500 dark:text-gray-400">Loading chart...</p>
            </div>
          </div>
        ) : (
          <Line data={chartData} options={{ ...defaultOptions, ...options }} />
        )}
      </div>
    </ChartContainer>
  );
});

ModernLineChart.displayName = "ModernLineChart";

// Modern Bar Chart Component
export const ModernBarChart = React.forwardRef(({
  className,
  data,
  options = {},
  title,
  subtitle,
  horizontal = false,
  showGrid = true,
  rounded = true,
  loading = false,
  ...props
}, ref) => {
  const hasMultipleDatasets = data.datasets?.length > 1;
  
  const defaultOptions = {
    responsive: true,
    maintainAspectRatio: false,
    indexAxis: horizontal ? 'y' : 'x',
    plugins: {
      legend: {
        display: hasMultipleDatasets,
        position: 'top',
        align: 'end',
        labels: {
          padding: 8, // Reduced padding
          usePointStyle: true,
          font: {
            size: 11, // Smaller font
            family: designTokens.typography.fontFamily.primary.join(', '),
          },
          color: designTokens.colors.neutral[500],
          boxWidth: 8, // Smaller legend boxes
          boxHeight: 8,
        },
      },
      tooltip: {
        backgroundColor: `${designTokens.colors.neutral[900]}e6`,
        titleColor: designTokens.colors.semantic.text.inverse,
        bodyColor: designTokens.colors.semantic.text.inverse,
        borderColor: `${designTokens.colors.neutral[200]}1a`,
        borderWidth: 1,
        cornerRadius: parseInt(designTokens.borderRadius.lg),
        padding: parseInt(designTokens.spacing[3]),
        titleFont: {
          size: 14,
          family: designTokens.typography.fontFamily.primary.join(', '),
          weight: designTokens.typography.fontWeight.semibold,
        },
        bodyFont: {
          size: 13,
          family: designTokens.typography.fontFamily.primary.join(', '),
        },
      },
    },
    scales: {
      x: {
        display: true,
        grid: {
          display: showGrid && !horizontal,
          color: `${designTokens.colors.neutral[400]}1a`,
          drawBorder: false,
        },
        ticks: {
          color: designTokens.colors.neutral[500],
          font: {
            size: 11,
            family: designTokens.typography.fontFamily.primary.join(', '),
          },
          padding: 8, // Reduce tick padding
        },
      },
      y: {
        display: true,
        grid: {
          display: showGrid && horizontal,
          color: `${designTokens.colors.neutral[400]}1a`,
          drawBorder: false,
        },
        ticks: {
          color: designTokens.colors.neutral[500],
          font: {
            size: 11,
            family: designTokens.typography.fontFamily.primary.join(', '),
          },
          padding: 8, // Reduce tick padding
        },
      },
    },
    elements: {
      bar: {
        borderRadius: rounded ? parseInt(designTokens.borderRadius.md) : 0,
        borderSkipped: false,
      },
    },
    layout: {
      padding: {
        left: 10,
        right: 10,
        top: hasMultipleDatasets ? 10 : 5, // Less top padding when no legend
        bottom: 10,
      },
    },
  };

  const chartData = {
    ...data,
    datasets: data.datasets?.map(dataset => ({
      ...dataset,
      borderRadius: rounded ? parseInt(designTokens.borderRadius.md) : 0,
      borderSkipped: false,
    })),
  };

  return (
    <ChartContainer ref={ref} className={className} {...props}>
      {(title || subtitle) && (
        <ChartHeader title={title} subtitle={subtitle} />
      )}
      <div className={cn(
        "relative",
        hasMultipleDatasets ? "h-96" : "h-80"
      )}>
        {loading ? (
          <div className="flex items-center justify-center h-full">
            <div className="text-center">
              <div 
                className="animate-spin rounded-full h-8 w-8 border-b-2 mx-auto mb-2"
                style={{ borderColor: designTokens.colors.primary[600] }}
              ></div>
              <p className="text-sm text-gray-500 dark:text-gray-400">Loading chart...</p>
            </div>
          </div>
        ) : (
          <Bar data={chartData} options={{ ...defaultOptions, ...options }} />
        )}
      </div>
    </ChartContainer>
  );
});

ModernBarChart.displayName = "ModernBarChart";

// Activity Heatmap Component
export const ActivityHeatmap = React.forwardRef(({
  className,
  data,
  title,
  subtitle,
  colorScale = [designTokens.colors.neutral[100], designTokens.colors.feedback.success],
  ...props
}, ref) => {
  const getHeatmapColor = (value, max) => {
    if (!value || value === 0) {
      // Use CSS custom properties for proper dark mode support
      return 'rgb(229, 231, 235)'; // gray-200 for light mode
    }
    
    const intensity = value / max;
    const baseColor = designTokens.colors.feedback.success; // #10b981
    
    // Create intensity-based colors
    if (intensity >= 0.8) return baseColor; // Full color
    if (intensity >= 0.6) return `${baseColor}cc`; // 80% opacity
    if (intensity >= 0.4) return `${baseColor}99`; // 60% opacity  
    if (intensity >= 0.2) return `${baseColor}66`; // 40% opacity
    return `${baseColor}33`; // 20% opacity
  };

  if (!data || !data.heatmap_data) {
    return (
      <ChartContainer ref={ref} className={className} {...props}>
        {(title || subtitle) && (
          <ChartHeader title={title} subtitle={subtitle} />
        )}
        <div className="flex items-center justify-center h-48 text-gray-500 dark:text-dark-600">
          No data available
        </div>
      </ChartContainer>
    );
  }

  const { heatmap_data, date_range, max_count } = data;

  // Generate dates and hours
  const startDate = new Date(date_range.start_date);
  const endDate = new Date(date_range.end_date);
  const dates = [];
  
  for (let d = new Date(startDate); d <= endDate; d.setDate(d.getDate() + 1)) {
    dates.push(new Date(d));
  }

  // Show only active hours from 7AM to 11PM (7-23) instead of all 24 hours
  const hours = [];
  for (let h = 7; h <= 23; h++) {
    hours.push(h);
  }

  // Create data lookup
  const dataLookup = {};
  heatmap_data.forEach(item => {
    const key = `${item.date}-${item.hour}`;
    dataLookup[key] = item.completed_count;
  });

  // Calculate dynamic height for 17 hours (7AM-11PM) with more compact sizing
  const dynamicHeight = Math.max(600, hours.length * 25 + 150); // Reduced: Minimum 600px, then 25px per hour + less padding

  return (
    <ChartContainer ref={ref} className={className} {...props}>
      {(title || subtitle) && (
        <ChartHeader title={title} subtitle={subtitle} />
      )}
      <div className="space-y-4" style={{ height: `${dynamicHeight}px`, minHeight: `${dynamicHeight}px` }}>
        <div className="grid grid-cols-[80px_1fr] gap-3 text-xs h-full"> {/* Increased label width and gap */}
          {/* Y-axis labels */}
          <div className="flex flex-col justify-between pt-4 pb-12"> {/* Reduced padding */}
            {hours.map(hour => (
              <div 
                key={hour} 
                className="h-5 flex items-center justify-end pr-3 text-gray-500 dark:text-gray-400 text-xs font-medium" // Reduced height back to h-5
              >
                {hour === 0 ? '12 AM' : hour === 12 ? '12 PM' : hour < 12 ? `${hour} AM` : `${hour - 12} PM`}
              </div>
            ))}
          </div>
          
          {/* Chart content */}
          <div className="h-full">
            {/* X-axis labels */}
            <div className="flex justify-between mb-2 text-gray-500 dark:text-gray-400 text-xs font-medium"> {/* Reduced margin bottom */}
              {dates.map((date, index) => (
                <div key={index} className="text-xs text-center min-w-0 flex-1">
                  {dates.length <= 7 || index % Math.ceil(dates.length / 7) === 0 
                    ? date.toLocaleDateString('en-US', { month: 'short', day: 'numeric' })
                    : ''
                  }
                </div>
              ))}
            </div>
            
            {/* Heatmap grid */}
            <div 
              className="grid gap-1"
              style={{ 
                gridTemplateColumns: `repeat(${dates.length}, 1fr)`,
                gridTemplateRows: `repeat(${hours.length}, 1fr)`,
                height: 'calc(100% - 50px)' // Reduced space subtraction
              }}
            >
              {hours.map(hour => 
                dates.map(date => {
                  const dateStr = date.toISOString().split('T')[0];
                  const key = `${dateStr}-${hour}`;
                  const count = dataLookup[key] || 0;
                  const color = getHeatmapColor(count, max_count);
                  
                  return (
                    <div
                      key={`${hour}-${dateStr}`}
                      className="rounded-sm border transition-all cursor-pointer hover:ring-2 hover:ring-blue-500 hover:z-10 relative bg-gray-200 dark:bg-gray-700 min-h-[18px]" // Reduced min-height
                      style={{ 
                        backgroundColor: count > 0 ? color : undefined,
                      }}
                      title={`${dateStr} ${hour}:00 - ${count} tasks`}
                    />
                  );
                })
              )}
            </div>
          </div>
        </div>
        
        {/* Legend */}
        <div className="flex items-center justify-center space-x-2 text-xs text-gray-500 dark:text-gray-400 pt-2 border-t border-gray-200 dark:border-gray-700">
          <span>Less</span>
          <div className="flex space-x-1">
            {[
              { color: 'bg-gray-200 dark:bg-gray-700', label: '0' },
              { color: `${designTokens.colors.feedback.success}33`, label: '1-2' },
              { color: `${designTokens.colors.feedback.success}66`, label: '3-4' },
              { color: `${designTokens.colors.feedback.success}99`, label: '5-6' },
              { color: `${designTokens.colors.feedback.success}cc`, label: '7-8' },
              { color: designTokens.colors.feedback.success, label: '9+' }
            ].map((item, index) => (
              <div
                key={index}
                className={`w-3 h-3 rounded-sm border border-gray-300 dark:border-gray-600 ${
                  index === 0 ? item.color : ''
                }`}
                style={{ 
                  backgroundColor: index === 0 ? undefined : item.color
                }}
              />
            ))}
          </div>
          <span>More</span>
        </div>
      </div>
    </ChartContainer>
  );
});

ActivityHeatmap.displayName = "ActivityHeatmap";

// Metric Card with Mini Chart
export const MetricCard = React.forwardRef(({
  className,
  value,
  label,
  change,
  changeType,
  icon,
  chartData,
  chartType = "line",
  trend,
  ...props
}, ref) => {
  const renderMiniChart = () => {
    if (!chartData) return null;

    const miniOptions = {
      responsive: true,
      maintainAspectRatio: false,
      plugins: {
        legend: { display: false },
        tooltip: { enabled: false },
      },
      scales: {
        x: { display: false },
        y: { display: false },
      },
      elements: {
        point: { radius: 0 },
        line: { borderWidth: 2 },
      },
    };

    if (chartType === "line") {
      return <Line data={chartData} options={miniOptions} />;
    }
    
    return <Bar data={chartData} options={miniOptions} />;
  };

  return (
    <div
      ref={ref}
      className={cn(
        "bg-white dark:bg-dark-200 rounded-xl p-6 border border-gray-200 dark:border-dark-300 shadow-sm hover:shadow-md transition-all duration-200",
        className
      )}
      {...props}
    >
      <div className="flex items-start justify-between mb-4">
        <div>
          <p className="text-sm font-medium text-gray-600 dark:text-dark-600 mb-1">
            {label}
          </p>
          <div className="flex items-baseline space-x-2">
            <p className="text-2xl font-bold text-gray-900 dark:text-dark-900">
              {value}
            </p>
            {change && (
              <span className={cn(
                "text-sm font-medium",
                changeType === "positive" ? "text-green-600" : "text-red-600"
              )}>
                {change}
              </span>
            )}
          </div>
        </div>
        {icon && (
          <div className="p-2 bg-blue-100 dark:bg-blue-500/20 text-blue-600 dark:text-blue-400 rounded-lg">
            {icon}
          </div>
        )}
      </div>
      
      {chartData && (
        <div className="h-12 -mx-2">
          {renderMiniChart()}
        </div>
      )}
      
      {trend && (
        <p className="text-xs text-gray-500 dark:text-dark-500 mt-2">
          {trend}
        </p>
      )}
    </div>
  );
});

MetricCard.displayName = "MetricCard";

// Peak Performance Hours Component
export const PeakPerformanceHours = React.forwardRef(({
  className,
  title = "Peak Performance Hours",
  data = [
    { timeRange: "9:00 AM - 11:00 AM", percentage: 85, color: designTokens.colors.feedback.success },
    { timeRange: "2:00 PM - 4:00 PM", percentage: 72, color: designTokens.colors.primary[600] },
    { timeRange: "7:00 PM - 9:00 PM", percentage: 58, color: designTokens.colors.feedback.warning }
  ],
  ...props
}, ref) => {
  return (
    <ChartContainer ref={ref} className={className} {...props}>
      <ChartHeader title={title} />
      <div className="space-y-4">
        {data.map((item, index) => (
          <div key={index} className="flex items-center justify-between">
            <span className="text-sm font-medium text-gray-700 dark:text-gray-300 min-w-0 flex-1">
              {item.timeRange}
            </span>
            <div className="flex items-center space-x-3 min-w-0 flex-1">
              <div className="flex-1 bg-gray-200 dark:bg-gray-700 rounded-full h-2">
                <div 
                  className="h-2 rounded-full transition-all duration-300"
                  style={{ 
                    width: `${item.percentage}%`, 
                    backgroundColor: item.color 
                  }}
                />
              </div>
              <span className="text-sm font-semibold text-gray-900 dark:text-white min-w-0">
                {item.percentage}%
              </span>
            </div>
          </div>
        ))}
      </div>
    </ChartContainer>
  );
});

PeakPerformanceHours.displayName = "PeakPerformanceHours";

// Focus Time Analysis Component
export const FocusTimeAnalysis = React.forwardRef(({
  className,
  title = "Focus Time Analysis",
  averageFocus = "4.2h",
  subtitle = "Average Daily Focus",
  activities = [
    { name: "Deep Work", value: 2.8, color: designTokens.colors.secondary[600] },
    { name: "Meetings", value: 1.4, color: designTokens.colors.primary[600] }
  ],
  ...props
}, ref) => {
  const maxValue = Math.max(...activities.map(a => a.value));

  return (
    <ChartContainer ref={ref} className={className} {...props}>
      <ChartHeader title={title} />
      <div className="text-center mb-6">
        <div className="text-4xl font-bold text-purple-600 dark:text-purple-400 mb-1">
          {averageFocus}
        </div>
        <div className="text-sm text-gray-500 dark:text-gray-400">
          {subtitle}
        </div>
      </div>
      <div className="space-y-4">
        {activities.map((activity, index) => (
          <div key={index} className="flex items-center justify-between">
            <span className="text-sm font-medium text-gray-700 dark:text-gray-300">
              {activity.name}
            </span>
            <div className="flex items-center space-x-3 flex-1 ml-4">
              <div className="flex-1 bg-gray-200 dark:bg-gray-700 rounded-full h-2">
                <div 
                  className="h-2 rounded-full transition-all duration-300"
                  style={{ 
                    width: `${(activity.value / maxValue) * 100}%`, 
                    backgroundColor: activity.color 
                  }}
                />
              </div>
              <span className="text-sm font-semibold text-gray-900 dark:text-white min-w-0">
                {activity.value}h
              </span>
            </div>
          </div>
        ))}
      </div>
    </ChartContainer>
  );
});

FocusTimeAnalysis.displayName = "FocusTimeAnalysis";

// Weekly Goals Progress Component
export const WeeklyGoalsProgress = React.forwardRef(({
  className,
  title = "Weekly Goals Progress",
  goals = [
    { name: "Complete 50 tasks", current: 42, target: 50, color: designTokens.colors.feedback.success },
    { name: "Attend 8 meetings", current: 6, target: 8, color: designTokens.colors.primary[600] },
    { name: "Create 20 notes", current: 23, target: 20, color: designTokens.colors.secondary[600] }
  ],
  ...props
}, ref) => {
  return (
    <ChartContainer ref={ref} className={className} {...props}>
      <ChartHeader title={title} />
      <div className="space-y-4">
        {goals.map((goal, index) => {
          const percentage = Math.min((goal.current / goal.target) * 100, 100);
          const isExceeded = goal.current > goal.target;
          
          return (
            <div key={index}>
              <div className="flex items-center justify-between mb-2">
                <span className="text-sm font-medium text-gray-700 dark:text-gray-300">
                  {goal.name}
                </span>
                <span className="text-sm font-semibold text-gray-900 dark:text-white">
                  {goal.current}/{goal.target}
                </span>
              </div>
              <div className="bg-gray-200 dark:bg-gray-700 rounded-full h-2">
                <div 
                  className={cn(
                    "h-2 rounded-full transition-all duration-300",
                    isExceeded && "rounded-r-none"
                  )}
                  style={{ 
                    width: `${percentage}%`, 
                    backgroundColor: goal.color 
                  }}
                />
                {isExceeded && (
                  <div 
                    className="h-2 rounded-r-full -mt-2 opacity-75"
                    style={{ 
                      width: `${((goal.current - goal.target) / goal.target) * 100}%`,
                      backgroundColor: goal.color,
                      marginLeft: '100%'
                    }}
                  />
                )}
              </div>
            </div>
          );
        })}
      </div>
    </ChartContainer>
  );
});

WeeklyGoalsProgress.displayName = "WeeklyGoalsProgress";

// Recent Activity Component
export const RecentActivity = React.forwardRef(({
  className,
  title = "Recent Activity",
  activities = [
    { 
      text: "Team standup meeting", 
      time: "2 hours ago", 
      icon: "fa-solid fa-calendar",
      iconColor: designTokens.colors.primary[600]
    },
    { 
      text: "Completed: Design review", 
      time: "4 hours ago", 
      icon: "fa-solid fa-check-circle",
      iconColor: designTokens.colors.feedback.success
    },
    { 
      text: "Added note: Project requirements", 
      time: "6 hours ago", 
      icon: "fa-solid fa-sticky-note",
      iconColor: designTokens.colors.secondary[600]
    }
  ],
  ...props
}, ref) => {
  return (
    <ChartContainer ref={ref} className={className} {...props}>
      <ChartHeader title={title} />
      <div className="space-y-4">
        {activities.map((activity, index) => (
          <div key={index} className="flex items-start space-x-3">
            <div 
              className="p-2 rounded-lg flex-shrink-0"
              style={{ 
                backgroundColor: `${activity.iconColor}20`,
                color: activity.iconColor 
              }}
            >
              <i className={cn(activity.icon, "text-sm")} />
            </div>
            <div className="flex-1 min-w-0">
              <p className="text-sm font-medium text-gray-900 dark:text-white">
                {activity.text}
              </p>
              <p className="text-xs text-gray-500 dark:text-gray-400">
                {activity.time}
              </p>
            </div>
          </div>
        ))}
      </div>
    </ChartContainer>
  );
});

RecentActivity.displayName = "RecentActivity";

// AI Insights Component
export const AIInsights = React.forwardRef(({
  className,
  title = "AI Insights",
  insights = [
    {
      text: "Your productivity peaked on Tuesday this week with 85% task completion.",
      emoji: "📈"
    },
    {
      text: "Consider scheduling fewer meetings on Fridays to boost focus time.",
      emoji: "🎯"
    },
    {
      text: "You've created 40% more notes this month compared to last month.",
      emoji: "📝"
    }
  ],
  ...props
}, ref) => {
  return (
    <div
      ref={ref}
      className={cn(
        "bg-gradient-to-br from-blue-50 to-purple-50 dark:from-blue-900/20 dark:to-purple-900/20 rounded-xl p-6 border border-blue-200 dark:border-blue-800/50",
        className
      )}
      {...props}
    >
      <div className="flex items-center space-x-2 mb-4">
        <div className="p-1.5 bg-blue-100 dark:bg-blue-900/30 rounded-lg">
          <i className="fa-solid fa-brain text-blue-600 dark:text-blue-400 text-sm" />
        </div>
        <h3 className="text-lg font-semibold text-gray-900 dark:text-white">
          {title}
        </h3>
      </div>
      <div className="space-y-3">
        {insights.map((insight, index) => (
          <div key={index} className="flex items-start space-x-3">
            <span className="text-lg flex-shrink-0 mt-0.5">
              {insight.emoji}
            </span>
            <p className="text-sm leading-relaxed text-gray-700 dark:text-gray-300">
              {insight.text}
            </p>
          </div>
        ))}
      </div>
    </div>
  );
});

AIInsights.displayName = "AIInsights"; 